import {
  PanelBody,
  TextControl,
  TextareaControl,
  ColorPalette,
  Notice
} from "@wordpress/components";
import { InspectorControls, MediaUpload, MediaUploadCheck } from "@wordpress/block-editor";
import { t } from "drupal";
import { useState, useEffect, useCallback } from "@wordpress/element";
import { handleUrlChange2, sanitizeInput, sanitizeKeepHTML, isValidDomain } from '../utilities/helpers';


function SquareCardSidePanel({
  title = '',
  content = '',
  link = '',
  linkText = '',
  backgroundColorClass = '',
  ariaLabel = '',
  onChangeTitle = () => { },
  onChangeContent = () => { },
  onChangeLink = () => { },
  onChangeLinkText = () => { },
  onChangeBackgroundColorClass = () => { },
  setAttributes
}) {

  /**
   * Connect color palette UI to class based SCSS in the theme.
   * backgroundOptions works with <ColorPalette/>, edit.js, and
   * _card.scss
   **/

  const backgroundOptions = [
    { name: 'Purple', color: '#32006e', class: 'purple' },
    { name: 'Dark Grey', color: '#404040', class: 'darkgrey' },
    { name: 'Lite Grey', color: '#e5e5e5', class: 'litegrey' },
    { name: 'White', color: '#ffffff', class: 'white' },
  ];


  const onChangeAriaLabel = (value) => setAttributes({ ariaLabel: value });

  const [errors, setErrors] = useState({});

  /**
   * If the user presses Enter or Tab, update the URL
   **/

  const handleKeyDown = useCallback((event) => {
    if ((event.key === 'Enter' || event.key === 'Tab') && link) {
      //handleUrlChange2 is from /deohs_gutenberg_blocks/blocks/utilities/helpers.js
      const updatedUrl = handleUrlChange2(link);
      if (updatedUrl !== link) { // Prevent unnecessary updates
        setAttributes({ link: updatedUrl });
      }
    }
  }, [link, setAttributes]);

  /*
   * For the notice alert of side panel, it must use useEffect instead of validation.js
  */

  useEffect(() => {
    const trimmedLink = link.trim();
    const trimmedTitle = title.trim();
    const trimmedLinkText = linkText.trim();

    setErrors({
      // URL is invalid if provided and not a valid domain
      link: trimmedLink ? !isValidDomain(trimmedLink) : false,

      // Title is always required
      title: !trimmedTitle,

      // CTA text is only required if link is provided
      linkText: trimmedLink ? !trimmedLinkText : false,
    });
  }, [link, title, linkText]);



  return (
    <InspectorControls>
      <PanelBody title={t("Square Card Settings")} initialOpen={true}>


        <label className="form-label">{t("Background Color")}</label>

        {/* Connect color palette UI to class based SCSS in the theme.
          * backgroundOptions works with <ColorPalette/>, edit.js, and
          * _card.scss**/}

        <ColorPalette
          label={t("Background Color")}
          colors={backgroundOptions}
          value={backgroundOptions.find(opt => opt.class === backgroundColorClass)?.color}
          onChange={(colorHexOrClass) => {
            const selected = backgroundOptions.find(opt => opt.color === colorHexOrClass);
            if (selected) {
              onChangeBackgroundColorClass(selected.class);
            }
          }}
        />


        {errors.title && <Notice status="error">{t("Card Title is required")}</Notice>}
        <TextControl
          label={t("Card Title")}
          value={title}
          placeholder={t("Card title")}
          required
          onChange={(value) => {
            onChangeTitle(value);
            setErrors((prev) => ({ ...prev, title: !value.trim() }));
          }}

        />

        <TextareaControl
          label={t("Card Content")}
          value={content}
          placeholder={t("Card content")}
          onChange={onChangeContent}
        />

          {errors.link && <Notice status="error">{t("Valid link URL is required")}</Notice>}
          <TextControl
            label={t("Card Link URL")}
            value={link}
            onChange={onChangeLink}
            className={errors.link ? "editor-field-error" : ""}
            onBlur={() => {
              const trimmed = link?.trim();
              if (trimmed) {
                const updatedUrl = handleUrlChange2(trimmed);
                if (updatedUrl !== trimmed) {
                  setAttributes({ link: updatedUrl });
                }
              }
            }}
            onKeyDown={handleKeyDown} // Uses useCallback for efficiency
            placeholder={t("/about or example.com")}
          /><br/>

        {errors.linkText && (
          <Notice status="error">{t("CTA text is required when URL is provided")}</Notice>
        )}
        <TextControl
          label={t("Card Link Text")}
          placeholder={t("Your call to action")}
          value={linkText}
          onChange={onChangeLinkText}
          className={errors.linkText ? "editor-field-error" : ""}
        />


      </PanelBody>
      <PanelBody title="Accessibility Settings">
        <TextControl
          label="Aria Label"
          value={ariaLabel || ""}
          onChange={onChangeAriaLabel}
          help="If you are using a generic phrase for your link text like learn more add text here so people on screen readers know where the link actually goes."
        />
      </PanelBody>
    </InspectorControls>
  );
}

export default SquareCardSidePanel;
